import { AsyncPipe, Location } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterLink, UrlSerializer } from '@angular/router';
import { IonIcon } from '@ionic/angular/standalone';
import { combineLatest, EMPTY, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { bufferTime, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { IApplicationTemplateMetadata } from 'src/app/model/thingie/templates';
import { IThingie } from 'src/app/model/thingie/thingie';
import { IProject } from 'src/app/model/user-management/project';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LogService } from 'src/app/services/loggers/logger.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { UINotificationService } from 'src/app/services/ui-notification/uinotification.service';
import { UserService } from 'src/app/services/user/user.service';
import {
  DASHBOARD_PAGE_CARDS_PROJECT_STORAGE_KEY,
  DASHBOARD_PAGE_CARDS_SIZE_STORAGE_KEY,
  DASHBOARD_PAGE_CARDS_SORTING_STORAGE_KEY,
  DASHBOARD_PAGE_GROUP_BY_STORAGE_KEY,
  DASHBOARD_PAGE_THINGIES_PROJECT_STORAGE_KEY,
  DASHBOARD_PAGE_THINGIES_SIZE_STORAGE_KEY,
  DASHBOARD_THINGIE_TAB_STORAGE_KEY,
  getDashboardSetting,
  IThingieSortingAndFilter,
  updateCurrentThingieTabId,
  updateDashboardSetting
} from 'src/app/utility/dashboard-helper';
import { createDefaultThingieListFilter } from 'src/app/utility/default-object-creator';
import { groupThingies } from 'src/app/utility/thingie-group-helper';
import { DefaultUrls } from 'src/assets/default.urls';
import { SubSink } from 'subsink';
import { PaginationComponent } from '../pagination/pagination.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ListThingiesFilterComponent } from './list-thingies-filter/list-thingies-filter.component';
import { ThingieCardViewComponent } from './thingie-card-view/thingie-card-view.component';

export interface DisplayThingie {
  thingieId: string;
  thingieName: string;
  project: string;
  projectId: string;
  runningProcessNames: string[];
  assignedDevices: {
    name: string;
    assignments: number;
    tooltip: string;
    id: string;
    type: string;
  }[];
  taskNames: string[];
  status: boolean;
  archived: boolean;
  dateCreated: string;
  applicationTemplateName: string;
  thingieStatus?: string;
  checked?: boolean;
  thingie: IThingie;

  /**
   * List of all replicates-ids of the experiment
   * which this display thingie belongs to
   * INCLUDING itself
   */
  allReplicateIdsOfExperiment: string[];

  /**
   * List of all replicates of the experiment
   * which this display thingie belongs to
   * INCLUDING itself
   */
  allReplicatesOfExperiment?: DisplayThingie[];

  /**
   * An ID representing the whole experiment
   *
   * IMPORTANT: This ID is the ID of the first thingie in the experiment
   */
  thingieGroupId: string;
}

export interface IGroupedThingies {
  name: string;
  thingies: DisplayThingie[];
  showGroupDetailToggle?: boolean;
  allGroupChecked?: boolean;
  pinned: boolean;
}


export interface IThingieListAction {
  type: 'REFRESH' | 'RESET';
  extraSortFilter: IThingieSortingAndFilter | undefined;
}

export type TabFilterId = 'all' | 'planned' | 'running' | 'finished' | 'archived' | 'pinned';

@Component({
  selector: 'app-list-thingies',
  templateUrl: './list-thingies.component.html',
  styleUrls: ['./list-thingies.component.scss'],
  imports: [SpinnerComponent, MaterialImportsModule, IonIcon, ListThingiesFilterComponent, ThingieCardViewComponent, PaginationComponent, RouterLink, AsyncPipe,]
})
export class ListThingiesComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly urls = DefaultUrls;

  @HostListener('window:beforeunload', ['$event'])
  private beforeUnload() {
    localStorage.setItem(DASHBOARD_THINGIE_TAB_STORAGE_KEY + '-' + this.currentUserID, this.tabString);
  }

  /**
   * @private
   */
  @ViewChildren(ThingieCardViewComponent)
  private _thingieCardViewComponents?: QueryList<ThingieCardViewComponent>;


  /**
   * @private
   */
  @ViewChild('filterTabGroup')
  private _filterTabGroup?: MatTabGroup;


  /**
   * Determines whether list view or card view is shown
   *
   * `thingies` => list view
   * `cards` => card view
   */
  @Input()
  public set viewMode(newMode: 'thingies' | 'cards' | undefined) {
    if (newMode === undefined) {
      return;
    }
    const oldMode = this._viewMode;
    this._viewMode = newMode;

    if (oldMode !== newMode) {
      let pageSize = '0';
      // on switching check if not grouped, then we need to call the api again for fresh data
      if (newMode === 'thingies') {
        pageSize = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_THINGIES_SIZE_STORAGE_KEY) || '10';
        updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_THINGIES_SIZE_STORAGE_KEY, pageSize);

        // on thingie list, always group
        this.groupByExperimentChecked = true;
      } else {
        // on switching back to cards, and check with last saved setting
        this.groupByExperimentChecked = false;

        const isChecked: string | null = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_GROUP_BY_STORAGE_KEY);
        if (isChecked !== null) {
          this.groupByExperimentChecked = JSON.parse(isChecked);
        }

        pageSize = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_SIZE_STORAGE_KEY) || '8';
        updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_SIZE_STORAGE_KEY, pageSize);
      }

      this.pageSize = parseInt(pageSize, 10);
      this.resetPage();
      // moving to page 1, to avoid empty pages
      void this.loadThingies(1, this.pageSize, this.groupByExperimentChecked);
    }
  }
  public get viewMode() {
    return this._viewMode;
  }
  private _viewMode: 'thingies' | 'cards' = 'cards';

  /**
   * Can be used to control which tab is shown
   */
  @Input()
  public set selectedTab(tab: TabFilterId | undefined) {
    if (!tab) {
      return;
    }

    this.selectedTab$.next(tab);
  }

  @Output()
  public selectedTabChange = new EventEmitter<TabFilterId>();


  @Input()
  public currentPage = 1;

  @Input()
  public pageSize = 3;

  @Input()
  public groupByExperimentChecked = true;

  private totalSize = 0;
  protected collectionSize = 0;
  protected loadingData = false;
  protected showPagination = false;

  protected allThingies$?: Observable<IGroupedThingies[]>;
  protected thingieCount$: Observable<number> = EMPTY;

  private _refreshListSignal = false;
  @Input()
  public set refreshListSignal(signal: boolean) {
    if (signal !== this._refreshListSignal) {
      this._refreshListSignal = signal;

      this.resetPage();
      // moving to page 1, to avoid empty pages
      void this.loadThingies(1, this.pageSize, this.groupByExperimentChecked);
    }
  }

  // for generating the UI
  protected readonly thingiesGroups = [
    { label: 'All', labelClass: 'tab-id-all', dataCy: 'dashboard__tab__all' },
    { label: 'Pinned', labelClass: 'tab-id-pinned', dataCy: 'dashboard__tab__pinned' },
    { label: 'Planned', labelClass: 'tab-id-planned', dataCy: 'dashboard__tab__planned' },
    { label: 'Running', labelClass: 'tab-id-running', dataCy: 'dashboard__tab__running' },
    { label: 'Finished', labelClass: 'tab-id-finished', dataCy: 'dashboard__tab__finished' },
    { label: 'Archived', labelClass: 'tab-id-archived', dataCy: 'dashboard__tab__archived' },
  ];

  protected selectedTab$ = new ReplaySubject<TabFilterId>(1);
  private subsink = new SubSink();
  private tabString = 'all';
  private currentUserID!: string;
  protected isEditor = signal<boolean>(false);
  protected isAdmin = signal<boolean>(false);

  protected projectList$ = new Subject<IProject[]>();
  protected templateList$ = new Subject<IApplicationTemplateMetadata[]>();

  private subscriptions = new SubSink();

  private dynamicFilters: IThingieSortingAndFilter | undefined = undefined;

  protected readonly RESET_ACTION: IThingieListAction = {
    type: 'RESET',
    extraSortFilter: undefined
  };

  constructor(
    private logService: LogService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private thingieService: ThingieService,
    private uiNotifictions: UINotificationService,
    private location: Location,
    private urlSerializer: UrlSerializer,
    private auth: AuthService
  ) {
    const tabsForRealtimeUpdate = ['all', 'running'];

    this.subscriptions.sink = this.thingieService.getThingieNewStateNotification().pipe(
      bufferTime(5000),
      filter(buf => buf.length > 0),
      switchMap(states => this.allThingies$?.pipe(map(thingies => ({ states, thingies }))) ?? of({ states, thingies: [] })),
      tap(({ states, thingies }) => {
        let seenThingies = new Set(thingies.flatMap(thingieGroup => thingieGroup.thingies.map(thingie => thingie.thingieId)));
        let newStates = states.filter(({ thingieId }) => !seenThingies.has(thingieId)).map(({ state }) => state);
        if (newStates.includes('RUNNING') && tabsForRealtimeUpdate.includes(this.tabString)) {
          this.resetPage();
          // moving to page 1, to avoid empty pages
          void this.loadThingies(1, this.pageSize, this.groupByExperimentChecked);
        }
      })
    )
      .subscribe();
  }

  public async ngOnInit(): Promise<void> {
    this.currentUserID = await this.userService.getCurrentUser().then(user => {
      this.isAdmin.set(user.admin);
      return user._id;
    });

    void this.loadThingies(this.currentPage, this.pageSize, this.groupByExperimentChecked);

    // bypass the editor role check for admin users
    if (!this.isAdmin()) {
      void this.auth.getProjectIdsWithEditorRole().then(projectIds => {
        this.isEditor.set(projectIds.length > 0);
      });
    } else {
      this.isEditor.set(true);
    }
  }

  public ngAfterViewInit(): void {
    this.subsink.sink = this.selectedTab$.subscribe(
      tabId => this.selectTab(tabId)
    );
  }

  public async ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.subsink.unsubscribe();
    await this._updateCurrentTabId();
  }

  /**
   * get the list of Thingies
   *
   */
  private async loadThingies(page: number, pageSize: number, groupByExperimentChecked: boolean) {
    // always load grouped for thingie list mode, or when selected
    const isGrouped = this.viewMode === 'thingies' || (groupByExperimentChecked !== undefined && groupByExperimentChecked === true) ? true : false;
    this.loadingData = true;
    try {
      const currentTab = this.tabString
        .toUpperCase();

      const filters:  { [key: string]: any } = {
        groupByExperiment: isGrouped,
        group: currentTab
      };

      let selectedProject = 'all';

      // save the setting
      if (this.viewMode === 'cards') {
        updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_SIZE_STORAGE_KEY, this.pageSize.toString());
        selectedProject = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_PROJECT_STORAGE_KEY ) || selectedProject;
      } else {
        updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_THINGIES_SIZE_STORAGE_KEY, this.pageSize.toString());
        selectedProject = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_THINGIES_PROJECT_STORAGE_KEY) || selectedProject;
      }

      if (selectedProject !== 'all') {
        filters.project = selectedProject;
      }

      if (this.viewMode === 'cards') {
        const sortingValue = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_SORTING_STORAGE_KEY);
        if (sortingValue !== null) {
          filters.sort = sortingValue;
        }
      } else {
        filters.sort = 'ALPHABETICAL';

        if (this.dynamicFilters !== undefined) {
          filters.filters = {};
          // apply the sorting and filtering
          Object.entries(this.dynamicFilters).forEach(([key, item]) => {
            if (item.sort !== 'NONE') {
              let _sort = item.sort + '_' + key.toUpperCase();
              if (_sort === 'ASC_CREATED') {
                _sort = 'FIRST_CREATED';
              } else if (_sort === 'DESC_CREATED') {
                _sort = 'LAST_CREATED';
              } else if (_sort === 'ASC_NAME') {
                _sort = 'ALPHABETICAL';
              }
              filters.sort = _sort;
            }

            if (item.value !== '') {
              filters.filters[key.toUpperCase()] = item.value;
            }
          });

          // convert the filters to string
          filters.filters = JSON.stringify(filters.filters);
        }
      }


      if (filters.filters === undefined) {
        delete filters.filters;
      }

      // update the pagination UI with the requested page
      // then after receiveing the data from server, the component needs to be updated as well
      // this UI Update will prevent double clicking on the button
      this.currentPage = page;

      // get raw data
      const thingiesList$$: Observable<Observable<DisplayThingie>[]> = this
        .thingieService
        .getRecentThingieList$$(page, pageSize, filters)
        .pipe(
          tap(() => {
            const mostRecentPagination = this.thingieService.getMostRecentPagination();

            this.totalSize      = mostRecentPagination.totalSize;
            this.collectionSize = mostRecentPagination.collectionSize;
            this.currentPage    = mostRecentPagination.currentPage;
            this.pageSize       = mostRecentPagination.pageSize;
            this.updateUrlForPagination(mostRecentPagination.currentPage);
            this.showPagination = true;

            setTimeout(() => {
              this.loadingData = false;
            }, 200);
          }),
          shareReplay({ refCount: true, bufferSize: 1 }),
        );

      this.thingieCount$ = thingiesList$$.pipe(map(() => this.totalSize));

      const groupedThingies$ = combineLatest([thingiesList$$, this.userService.getSavedPins()]).pipe(
        // Ensure only the most-recent emission of groupThingies' returned
        // observable is used
        switchMap(([displayThingies$, savedPinsMap]) => groupThingies(isGrouped, displayThingies$, savedPinsMap))
      );

      this.allThingies$ = groupedThingies$.pipe(map(groupList =>
        groupList.filter(group => group.thingies.length)));
    } catch (err) {
      this.loadingData = false;
      this.logService.error(err);
      this.uiNotifictions.displayErrorAlert({
        title: 'Error',
        message: 'Failed to list thingies',
        details: err instanceof Error ? err.message : undefined
      }, err);
    }
  }

  protected changeMode(event: MatButtonToggleChange) {
    const newMode = event.value;

    void this.router.navigateByUrl(DefaultUrls.route.dashboardMode(newMode));
  }

  protected onIndexSelected(index: number) {
    const prevousTab = this.tabString;
    this.tabString = this.tabValues(index)!;

    void this._updateCurrentTabId();

    if (this.currentPage > 1 || prevousTab !== this.tabString) {

      // on tab switch, reset the filters
      this.dynamicFilters = createDefaultThingieListFilter();

      // force to go to first page on tab switch
      this.resetPage();
      void this.loadThingies(1, this.pageSize, this.groupByExperimentChecked);
    }

    void this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: { tab: this.tabString },
        queryParamsHandling: 'merge',
      }
    );
  }

  private async _updateCurrentTabId() {
    const currentUserID = await this.userService.getCurrentUser().then(user => user._id);
    updateCurrentThingieTabId(currentUserID, this.tabString);
  }

  private tabValues(index: number) {
    switch (index) {
      case 0:
        return 'all';
      case 1:
        return 'pinned';
      case 2:
        return 'planned';
      case 3:
        return 'running';
      case 4:
        return 'finished';
      case 5:
        return 'archived';
      default:
        return;
    }
  }

  private selectTab(tabId: TabFilterId) {
    if (!this._filterTabGroup) {
      return;
    }

    let tabIndex = 0;
    const tab = this._filterTabGroup._allTabs.find((tab, idx) => {
      if (tab.labelClass === `tab-id-${tabId}`) {
        tabIndex = idx;
        return true;
      }
      return false;
    });

    if (!tab) {
      this.logService.warn('Invalid tab option chosen.');
      return;
    }

    this._filterTabGroup.selectedIndex = tabIndex;
  }
  protected paginationCallbackFunction(currentPage: number) {
    if (this.currentPage === currentPage) {
      return;
    }

    this.resetPage();
    void this.loadThingies(currentPage, this.pageSize, this.groupByExperimentChecked);
  }

  protected paginationPageSizeCallbackFunction(selectedPageSize: number) {
    if (this.pageSize === selectedPageSize) {
      return;
    }

    // hide the pagination to avoid invalid pages to be clicked
    this.showPagination = false;

    this.pageSize = selectedPageSize;
    this.resetPage();
    // moving to page 1, to avoid empty pages
    void this.loadThingies(1, this.pageSize, this.groupByExperimentChecked);
  }


  protected filterCallbackFunction(_filter: IThingieListAction) {
    this.resetPage();

    // refresh the current page
    let _page = this.currentPage;

    if (_filter.type === 'RESET') {
      // moving to page 1, to avoid empty pages
      _page = 1;
      this.dynamicFilters = _filter.extraSortFilter;
    }

    void this.loadThingies(_page, this.pageSize, this.groupByExperimentChecked);
  }

  protected groupByExperimentCallbackFunction(isChecked: boolean) {
    this.groupByExperimentChecked = isChecked;
    updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_GROUP_BY_STORAGE_KEY, isChecked.toString());

    this.resetPage();
    // moving to page 1, to avoid empty pages
    void this.loadThingies(1, this.pageSize, this.groupByExperimentChecked);
  }

  private resetPage() {
    this.loadingData = true;
    this.thingieCount$ = EMPTY;

    this.allThingies$ = of([]);
    this._thingieCardViewComponents?.forEach(view => view.resetCards());
  }

  private updateUrlForPagination(currentPage: number) {
    const tree = this.router.createUrlTree([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
      preserveFragment: true });

    this.location.replaceState(`${this.urlSerializer.serialize(tree)}`);
  }
}
